import { createSelector, createSlice, current } from "@reduxjs/toolkit";
import { fetchSecondaryFilter } from "../../api";
import { getDataFromUrl } from "../../utilities/urlResolver";
import translate from "../../utilities/translator";
import abTesting from "../../services/abTesting";
import { getCookie } from '../../utilities/helpers';

const initialState = {
  pending: true,
  error: false,
  data: {
    facilities: [],
  },
  isClean: {
    sort: true,
    stars: true,
    rating: true,
    board: true,
    stops: true,
    facilities: true,
    duration: true,
    freeCancellation: true,
    luggage: true,
    flex: true,
  },
  lastChangedFilter: ''
};

export const secondaryFilterSlice = createSlice({
  name: "secondaryFilter",
  initialState,
  reducers: {
    updateSecondaryFilter: (state, action) => {
      const { updatedValue, filter, noChangeLastChanged = false } = action.payload;
      return {
        ...state,
        data: { ...state.data, [filter]: updatedValue },
        lastChangedFilter: (updatedValue && filter && !noChangeLastChanged) ? filter : ''
      };
    },
    updateAllSecondaryFilter: (state, action) => {
      const { updatedFilters, lastChangedFilter } = action.payload;
      return {
        ...state,
        data: updatedFilters,
        lastChangedFilter
      };
    },
    getSecondaryFilterSuccess: (state, action) => {
      const {
        board,
        duration,
        facilities,
        rating,
        sort,
        stars,
        stops,
        luggage,
        flex,
      } = action.payload.filters;

      const { secondaryFilter: urlData } = getDataFromUrl('secondaryFilter');

      const luggageRateDefault = [
        {
          value: 'canAddTrolley',
          label: translate('holidayFinder.secondaryFilter.canAddTrolley'),
          active: true
        }
      ];

      const filter = {
        sort: sort.map((i) => ({
          value: Object.values(i)[0],
          label: Object.keys(i)[0],
          active: urlData.sort === Object.keys(i)[0]
        })),
        stars: stars.map((i) => ({
          value: i,
          label: i,
          active: urlData.stars.some((item) => item === i),
        })),
        rating: rating.map((i) => ({
          value: i,
          label: i,
          active: urlData.rating.some((item) => item === i),
        })),
        board: board.map((i) => {
          const label =
            i === "ro"
              ? translate("holidayFinder.dealItem.room_only")
              : i === "bb"
              ? translate("holidayFinder.dealItem.breakfast_included")
              : i === "ai"
              ? translate("holidayFinder.dealItem.all_inclusive")
              : i === "hb"
              ? translate("holidayFinder.dealItem.half_board")
              : i === "fb"
              ? translate("holidayFinder.dealItem.full_board")
              : i;
          return {
            value: i,
            label,
            active: urlData.board.some((board) => board === i),
          };
        }),
        stops: stops.map((i) => ({
          value: i,
          label:
            i === 0
              ? translate("holidayFinder.secondaryFilter.Nonstop")
              : i === 1
              ? translate("holidayFinder.secondaryFilter.One_stop")
              : translate("holidayFinder.secondaryFilter.Two_stops") + "+",
          active: urlData.stops.some((stop) => stop === i),
        })),
        facilities: Object.keys(facilities).map((key) => ({
          value: key,
          label: facilities[key],
          active: urlData.facilities.some((facility) => facility === Number(key)),
        })),
        duration: {
          minTime: duration.minTime,
          activeTime: urlData.duration || duration.maxTime,
          maxTime: duration.maxTime,
        },
        freeCancellation: urlData.freeCancellation,
        luggage: (luggage && luggage.length && abTesting.moveMultiRates) ?
          luggage.map((bagType) => ({
            value: bagType,
            label: translate(`holidayFinder.secondaryFilter.${bagType}`),
            active: urlData.luggage.some((bag) => bag === bagType)
          })) :
          luggageRateDefault,
        flex: {
          available: flex,
          active: urlData.flex,
        },
      };

      if (abTesting.userPrefEnabled) {
        filter.sort.push({ value: 1, label: 'user_pref', active: !!(urlData.sort === 'user_pref') });
        if (urlData.sort === 'user_pref') {
          filter.sort[0].active = false;
        }
      }

      if (!filter.sort.some(({ active }) => active)) {
        filter.sort[0].active = true;
      }

      return {
        ...state,
        data: filter,
        pending: false,
        error: false,
      };
    },
    updateSecondaryFilterFromUrl: state => {
      const currentState = current(state);

      const {
        board,
        duration,
        facilities,
        rating,
        sort,
        stars,
        stops,
        freeCancellation,
        luggage,
        flex,
      } = currentState.data;
      const { secondaryFilter: urlData } = getDataFromUrl();

      return {
        ...currentState,
        data: {
          sort: sort.map(i => ({
            ...i,
            active: urlData.sort === i.label
          })),
          stars: stars.map((i) => ({
            ...i,
            active: urlData.stars.some((item) => item === i.value),
          })),
          rating: rating.map((i) => ({
            ...i,
            active: urlData.rating.some((item) => item === i.value),
          })),
          board: board.map((i) => ({
            ...i,
            active: urlData.board.some((board) => board === i.value),
          })),
          stops: stops.map((i) => ({
            ...i,
            active: urlData.stops.some((stop) => stop === i.value),
          })),
          facilities: facilities.map((i) => ({
            ...i,
            active: urlData.facilities.some((facility) => +facility === +i.value),
          })),
          duration: {
            ...duration,
            activeTime: urlData.duration || duration.activeTime
          },
          freeCancellation: urlData.freeCancellation || freeCancellation,
          luggage: luggage.map((i) => ({
            ...i,
            active: urlData.luggage.some((bag) => bag === i.value),
          })),
          flex: abTesting.moveMultiRates ? { available: true, active: urlData.flex } : false,
        }
      }
    },
    getSecondaryFilterFailed: (state) => {
      return {
        ...state,
        pending: false,
        error: true,
      };
    },
    setSecondaryFilterDefault: (state) => {
      const currentState = current(state);
      const {
        board,
        duration,
        facilities,
        rating,
        sort,
        stars,
        stops,
        luggage,
        flex,
      } = currentState.data;

      return {
        ...state,
        data: {
          sort,
          stars: stars.map(i => ({ ...i, active: false })),
          rating: rating.map(i => ({ ...i, active: false })),
          board: board.map(i => ({ ...i, active: false })),
          stops: stops.map(i => ({ ...i, active: false })),
          facilities: facilities.map(i => ({ ...i, active: false, value: Number(i.value) })),
          duration: {
            ...duration,
            activeTime: duration.maxTime
          },
          freeCancellation: false,
          luggage: abTesting.moveMultiRates ? luggage.map(i => ({ ...i, active: false })) : luggage.map(i => ({ ...i, active: (i.value === 'canAddTrolley') })),
          flex: { ...flex, active: false },
        }
      }
    },
    setSecondaryHotelFilterDefault: (state) => {
      // reset only hotel filters (without flight) - for the search hotel by name
      const currentState = current(state);
      const {
        board,
        duration,
        facilities,
        rating,
        sort,
        stars,
        stops,
        luggage,
        flex,
      } = currentState.data;

      return {
        ...state,
        data: {
          sort,
          stars: stars.map(i => ({ ...i, active: false })),
          rating: rating.map(i => ({ ...i, active: false })),
          board: board.map(i => ({ ...i, active: false })),
          stops: stops,
          facilities: facilities.map(i => ({ ...i, active: false, value: Number(i.value) })),
          duration: duration,
          freeCancellation: false,
          luggage: luggage,
          flex: { ...flex, active: false },
        }
      }
    },
    updateCleanFilters: (state) => {
      const { data } = current(state);
      const {
        board,
        duration,
        facilities,
        rating,
        sort,
        stars,
        stops,
        freeCancellation,
        luggage,
        flex,
      } = data;

      state.isClean = {
        board: !board.some(({ active }) => active),
        duration: duration.activeTime === duration.maxTime,
        facilities: !facilities.some(({ active }) => active),
        rating: !rating.some(({ active }) => active),
        sort: sort[0].active,
        stars: !stars.some(({ active }) => active),
        stops: !stops.some(({ active }) => active ),
        freeCancellation: !freeCancellation,
        luggage: !(luggage && luggage.some(({ active }) => active)),
        flex: !(flex && flex.active),
      }
    }
  },
});

export const {
  updateSecondaryFilter,
  updateAllSecondaryFilter,
  getSecondaryFilterFailed,
  getSecondaryFilterSuccess,
  updateSecondaryFilterFromUrl,
  setSecondaryFilterDefault,
  setSecondaryHotelFilterDefault,
  updateCleanFilters,
} = secondaryFilterSlice.actions;

export const fetchSecondaryFilterData = () => async (dispatch) => {
  try {
    const response = await fetchSecondaryFilter();
    dispatch(getSecondaryFilterSuccess(response.data));
  } catch (error) {
    dispatch(getSecondaryFilterFailed(error));
  }
};

export const selectSecondaryFilter = (state) => state.secondaryFilter;
export const selectIsSecondaryFilterClear = (state) => Object.values(state.secondaryFilter.isClean).every(item => item)
  && (!state.search.when.nights || state.search.when.nights.length === 0);

export const selectSecondaryFilterCleanStatus = createSelector(
  selectSecondaryFilter,
  (state) => {
    const data = state.data;

    return {
      stars: !data.stars.some(({ active }) => active),
      rating: !data.rating.some(({ active }) => active),
      board: !data.board.some(({ active }) => active),
      stops: !data.stops.some(({ active }) => active),
      facilities: !data.facilities.some(({ active }) => active),
      duration: data.duration.activeTime === data.duration.maxTime,
      freeCancellation: !data.freeCancellation,
      luggage: !data.luggage.some(({ active }) => active),
      flex: !data.flex.active,
    }
  }
)

export const selectActiveSort = createSelector(
  selectSecondaryFilter,
  (state) => state.data.sort.find(({ active }) => active)
);

export default secondaryFilterSlice.reducer;
